//! A table in the grid.

import type { Sheet } from '@/app/grid/sheet/Sheet';
import { TableHeader } from '@/app/gridGL/cells/tables/TableHeader';
import { TableOutline } from '@/app/gridGL/cells/tables/TableOutline';
import { TablePivotFieldControls } from '@/app/gridGL/cells/tables/TablePivotFieldControls';
import { TablePivotOutlineControls } from '@/app/gridGL/cells/tables/TablePivotOutlineControls';
import type { TablePointerDownResult } from '@/app/gridGL/cells/tables/Tables';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { htmlCellsHandler } from '@/app/gridGL/HTMLGrid/htmlCells/htmlCellsHandler';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { getFrozenPaneForCell } from '@/app/gridGL/pixiApp/frozenLayout';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { embedClampToRange } from '@/app/helpers/isEmbed';
import type { JsCoordinate, JsRenderCodeCell } from '@/app/quadratic-core-types';
import type { Point } from 'pixi.js';
import { Container, Rectangle } from 'pixi.js';

export class Table extends Container {
  public active = false;
  outline: TableOutline;

  // Collapse/expand controls; only draws anything for pivot tables.
  pivotOutlineControls: TablePivotOutlineControls;

  // Field-header filter dropdown buttons; only draws anything for pivot tables.
  pivotFieldControls: TablePivotFieldControls;

  // Header is either a child of Table or, when it is sticky, a child of
  // pixiApp.overHeadings.
  header: TableHeader;

  // whether the column headers are in the overHeadings container
  inOverHeadings = false;

  sheet: Sheet;
  tableBounds: Rectangle;
  codeCell: JsRenderCodeCell;
  tableCursor: string | undefined;

  imageHtmlGridBounds?: [number, number];

  // whether the code cell is currently running
  running: boolean | 'awaiting' = false;

  constructor(sheet: Sheet, codeCell: JsRenderCodeCell) {
    super();
    this.codeCell = codeCell;
    this.sheet = sheet;
    this.tableBounds = this.sheet.getScreenRectangle(
      this.codeCell.x,
      this.codeCell.y,
      this.codeCell.spill_error ? 0 : this.codeCell.w,
      this.codeCell.spill_error ? 0 : this.codeCell.h
    );
    this.header = this.addChild(new TableHeader(this));
    this.outline = this.addChild(new TableOutline(this));
    this.pivotOutlineControls = this.addChild(new TablePivotOutlineControls(this));
    this.pivotFieldControls = this.addChild(new TablePivotFieldControls(this));
    this.updateCodeCell(codeCell);
  }

  destroy() {
    this.hoverTableHeaders?.removeChild(this.header);
    const cellsMarkers = content.cellsSheets.getById(this.sheet.id)?.cellsMarkers;
    if (cellsMarkers) {
      cellsMarkers.remove(this.codeCell.x, this.codeCell.y);
    }
    this.sheet.gridOverflowLines.updateImageHtml(this.codeCell.x, this.codeCell.y);

    super.destroy();
  }

  get hoverTableHeaders(): Container {
    const cellsSheet = content.cellsSheets.getById(this.sheet.id);
    if (!cellsSheet) {
      throw new Error('Expected cellsSheet to be defined in Table.ts');
    }
    return cellsSheet.tables.hoverTableHeaders;
  }

  updateCodeCell = (codeCell?: JsRenderCodeCell) => {
    if (codeCell) {
      this.codeCell = codeCell;
      // fresh data supersedes any optimistic collapse-control state
      this.pivotOutlineControls.clearPendingToggles();
    }
    this.tableBounds = this.sheet.getScreenRectangle(
      this.codeCell.x,
      this.codeCell.y,
      this.codeCell.spill_error ? 1 : this.codeCell.w,
      this.codeCell.spill_error ? 1 : this.codeCell.h
    );
    this.position.set(this.tableBounds.x, this.tableBounds.y);
    this.updateVisibility();
    this.header.update(false);
    this.outline.update();
    this.pivotOutlineControls.update();
    this.pivotFieldControls.update();

    const cellsSheet = content.cellsSheets.getById(this.sheet.id);
    if (cellsSheet) {
      cellsSheet.cellsFills.updateAlternatingColors(
        this.codeCell.x,
        this.codeCell.y,
        this.codeCell.alternating_colors ? this.codeCell : undefined
      );
    }

    const cellsMarkers = content.cellsSheets.getById(this.sheet.id)?.cellsMarkers;
    if (!cellsMarkers) {
      console.log('Expected CellsMarkers to be defined in sheet');
      return;
    }

    if (this.codeCell.state === 'RunError' || this.codeCell.state === 'SpillError') {
      const box = this.sheet.getCellOffsets(this.codeCell.x, this.codeCell.y);
      cellsMarkers.add(box, this.codeCell);
    } else {
      cellsMarkers.remove(this.codeCell.x, this.codeCell.y);
    }

    if (!this.codeCell.spill_error && this.codeCell.state === 'Image') {
      this.sheet.gridOverflowLines.updateImageHtml(this.codeCell.x, this.codeCell.y, this.codeCell.w, this.codeCell.h);
    } else if (!this.codeCell.spill_error && this.codeCell.show_name) {
      this.sheet.gridOverflowLines.updateImageHtml(this.codeCell.x, this.codeCell.y, this.codeCell.w, 1);
    } else {
      this.sheet.gridOverflowLines.updateImageHtml(this.codeCell.x, this.codeCell.y);
    }
  };

  // True when the table anchor sits in pinned frozen rows/columns. Headers there
  // must stay on the grid so FrozenPanes can render them with the pane transform
  // instead of floating in hoverTableHeaders (which scrolls with the body).
  private isHeaderInFrozenArea = (): boolean => {
    return getFrozenPaneForCell(this.codeCell.x, this.codeCell.y) !== 'body';
  };

  /// Based on any arbitrary viewport bounds, returns the bounds of the table header if it would be floating
  calculateHeadingBounds = (bounds: Rectangle): Rectangle | undefined => {
    const gridHeading = content.headings.headingSize.unscaledHeight;
    const codeCell = this.codeCell;

    // return undefined if the table is not floating
    if (
      codeCell.is_html ||
      (!codeCell.show_name && !codeCell.show_columns) ||
      this.isHeaderInFrozenArea() ||
      this.tableBounds.top >= bounds.top + gridHeading
    ) {
      return;
    }

    const y = Math.min(this.header.bottomOfTable, this.tableBounds.y + bounds.top + gridHeading - this.tableBounds.top);
    return new Rectangle(this.tableBounds.x, y, this.tableBounds.width, this.header.height);
  };

  private updateVisibility = () => {
    const bounds = pixiApp.viewport.getClampedVisibleBounds();
    if (!intersects.rectangleRectangle(this.tableBounds, bounds)) {
      this.visible = false;
      this.header.visible = false;
    } else {
      this.visible = true;
      this.header.visible = true;
    }
  };

  private headingPosition = (bounds: Rectangle, gridHeading: number) => {
    const codeCell = this.codeCell;
    // When embed clamp is active, use unclamped bounds to decide floating.
    // The clamped bounds' top equals the content top, which always triggers
    // floating since gridHeading > 0. The actual viewport position tells us
    // whether the user has genuinely scrolled past the header.
    const effectiveBounds = embedClampToRange ? pixiApp.viewport.getVisibleBounds() : bounds;
    if (
      !this.isHeaderInFrozenArea() &&
      !codeCell.is_html &&
      (codeCell.show_name || codeCell.show_columns) &&
      this.tableBounds.top < effectiveBounds.top + gridHeading
    ) {
      this.header.toHover(effectiveBounds, gridHeading);
      this.inOverHeadings = true;
    } else {
      this.header.toGrid();
      this.inOverHeadings = false;
    }
  };

  // Checks whether the mouse cursor is hovering over the table or the table name
  checkHover = (world: Point): boolean => {
    return intersects.rectanglePoint(this.tableBounds, world);
  };

  update(bounds: Rectangle, gridHeading: number) {
    if (!intersects.rectangleRectangle(this.tableBounds, bounds)) {
      this.visible = false;
      this.header.visible = false;
      return;
    }
    if (
      (this.codeCell.show_name || this.codeCell.show_columns) &&
      this.codeCell.state !== 'RunError' &&
      this.codeCell.state !== 'SpillError'
    ) {
      this.header.visible = true;
    }
    this.visible = true;
    this.headingPosition(bounds, gridHeading);
    if (this.inOverHeadings) this.header.update(true);
  }

  private activate = (active: boolean) => {
    if (active === this.active) return;
    this.active = active;
    this.outline.update();
    this.header.update(false);
  };

  setHeaderHovered = (hovered: boolean) => {
    this.header.setHeaderHovered(hovered);
  };

  hideHeaderButtons = () => {
    this.header.hideHeaderButtons();
  };

  showActive() {
    this.activate(true);
    htmlCellsHandler.showActive(this.codeCell);
    this.header.updateSelection();
    pixiApp.setViewportDirty();
  }

  hideActive() {
    this.activate(false);
    htmlCellsHandler.hideActive(this.codeCell);
    this.header.updateSelection();
    pixiApp.setViewportDirty();
  }

  showColumnHeaders() {
    this.header.showColumnHeaders();
  }

  hideColumnHeaders(index: number) {
    this.header.hideColumnHeaders(index);
  }

  intersectsTableName(world: Point): TablePointerDownResult | undefined {
    return this.header.intersectsTableName(world);
  }

  getTableNameBounds(ignoreOverHeadings = false): Rectangle | undefined {
    if (!this.codeCell.show_name) {
      return;
    }
    const bounds = this.header.getTableNameBounds()?.clone();
    if (!ignoreOverHeadings && this.inOverHeadings) {
      const bounds = pixiApp.viewport.getVisibleBounds();
      bounds.y = bounds.top;
    }
    return bounds;
  }

  // Gets the column header bounds
  getColumnHeaderBounds(index: number): Rectangle | undefined {
    return this.header.getColumnHeaderBounds(index);
  }

  getSortDialogPosition(): JsCoordinate | undefined {
    return this.header.getSortDialogPosition();
  }

  pointerMove(world: Point): 'table-name' | boolean {
    if (this.pivotOutlineControls.pointerMove(world)) {
      this.tableCursor = this.pivotOutlineControls.tableCursor;
      this.setHeaderHovered(false);
      return true;
    }
    if (this.pivotFieldControls.pointerMove(world)) {
      this.tableCursor = this.pivotFieldControls.tableCursor;
      this.setHeaderHovered(false);
      return true;
    }
    const name = this.intersectsTableName(world);
    this.outline.update();
    this.setHeaderHovered(name !== undefined);
    if (name?.type === 'run-code') {
      this.tableCursor = 'pointer';
      return 'table-name';
    } else if (name?.type === 'dropdown') {
      this.tableCursor = 'pointer';
      return 'table-name';
    } else if (name?.type === 'table-name') {
      this.tableCursor = undefined;
      return 'table-name';
    }
    const result = this.header.pointerMove(world);
    if (result) {
      this.tableCursor = this.header.tableCursor;
    } else {
      this.tableCursor = undefined;
    }
    return result;
  }

  pointerDown(world: Point, isRightClick: boolean): TablePointerDownResult | undefined {
    if (!isRightClick) {
      const outlineControl = this.pivotOutlineControls.pointerDown(world);
      if (outlineControl) return outlineControl;
      const fieldControl = this.pivotFieldControls.pointerDown(world);
      if (fieldControl) return fieldControl;
    }
    return this.header.pointerDown(world, isRightClick);
  }

  pointerDownChart(world: Point): boolean {
    return this.codeCell.is_html_image && intersects.rectanglePoint(this.tableBounds, world);
  }

  // resizes an image or html table to its overlapping size
  resize(width: number, height: number) {
    this.tableBounds.width = width;
    this.tableBounds.height = height;
    this.outline.update();
    this.header.update(false);
  }
}
